import { useCallback, useEffect, useRef, useState } from 'react';
import userSettingsService from '../../services/userSettings.service';
import type { PanelTrashMap } from './panelLayoutTrash';
import { PanelLayoutItem, PanelLayouts, PANEL_GRID_VERSION, readPanelSetting } from './panelGridTypes';

const RETRY_MS = 15000;
const SAVE_DEBOUNCE_MS = 500;

export interface PanelTrashResult {
  layouts: PanelLayouts;
  trash: PanelTrashMap<PanelLayoutItem>;
}

/**
 * Per-user persistence for a panel grid's geometry, in one user_settings key.
 *
 * A FAILED load must never mark the layout as loaded: `loaded` unblocks the auto-save,
 * and the state it would save is the code default — so treating a failure as "loaded"
 * would persist the default over the user's real arrangement. Saving stays blocked until
 * a load succeeds; failed loads retry on a timer.
 *
 * The refs mirror the state synchronously so a caller can compute a trash/restore result
 * from the latest values inside an event handler without waiting for a render.
 *
 * @param settingKey Must be stable for the hook's lifetime; changing it at runtime is not
 *   supported (a save could write the previous key's layout under the new key).
 * @param getDefaults The code default layouts, shown until a load succeeds.
 * @param onSaveError Called when a debounced save rejects, so the page can toast; the local
 *   state is kept and the next change retries.
 */
export function usePanelLayoutPersistence(
  settingKey: string,
  getDefaults: () => PanelLayouts,
  onSaveError?: (err: unknown) => void,
) {
  const [panelLayouts, setPanelLayoutsState] = useState<PanelLayouts>(getDefaults);
  const panelLayoutsRef = useRef<PanelLayouts>(panelLayouts);
  const [panelTrash, setPanelTrashState] = useState<PanelTrashMap<PanelLayoutItem>>({});
  const panelTrashRef = useRef<PanelTrashMap<PanelLayoutItem>>(panelTrash);
  const [loaded, setLoaded] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [loadAttempt, setLoadAttempt] = useState(0);
  // Held in a ref so an inline arrow from the page does not re-arm the debounced save effect.
  const onSaveErrorRef = useRef(onSaveError);
  onSaveErrorRef.current = onSaveError;

  const setPanelLayouts = useCallback((next: PanelLayouts) => {
    panelLayoutsRef.current = next;
    setPanelLayoutsState(next);
  }, []);

  const setPanelTrash = useCallback((next: PanelTrashMap<PanelLayoutItem>) => {
    panelTrashRef.current = next;
    setPanelTrashState(next);
  }, []);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const setting = await userSettingsService.getUserSetting(settingKey);
        if (cancelled) return;
        const { layouts, trash } = readPanelSetting(setting?.setting_value);
        // If the first load failed and the user rearranged the default during the retry
        // window, a successful retry replaces that with the server layout — correct, they
        // were arranging the default, not their real board.
        if (layouts) setPanelLayouts(layouts);
        setPanelTrash(trash);
        setLoadFailed(false);
        setLoaded(true);
      } catch (err) {
        if (cancelled) return;
        console.error(`Failed to load ${settingKey} — panel saving stays blocked:`, err);
        setLoadFailed(true);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [settingKey, loadAttempt, setPanelLayouts, setPanelTrash]);

  useEffect(() => {
    if (!loadFailed) return;
    const timer = setTimeout(() => setLoadAttempt((a) => a + 1), RETRY_MS);
    return () => clearTimeout(timer);
  }, [loadFailed, loadAttempt]);

  const savePanelSetting = useCallback(
    (layouts: PanelLayouts, trash: PanelTrashMap<PanelLayoutItem>) =>
      userSettingsService.saveUserSetting(settingKey, {
        panelLayouts: layouts,
        version: PANEL_GRID_VERSION,
        panelTrash: trash,
      }),
    [settingKey],
  );

  // A successful load triggers one save of the just-loaded state ~500ms later. Intentional: it
  // creates the row for a new user and normalizes a blob whose corrupt workspaces
  // readPanelSetting dropped. A pending save is dropped on unmount (navigate away within
  // 500ms of a drag loses that drag) — same as LC; not worth a flush.
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (!loaded) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      savePanelSetting(panelLayouts, panelTrash)
        .catch((err) => {
          console.error('Failed to save panel layouts:', err);
          onSaveErrorRef.current?.(err);
        });
    }, SAVE_DEBOUNCE_MS);
    return () => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
    };
  }, [panelLayouts, panelTrash, loaded, savePanelSetting]);

  /** Apply a trash/restore/copy result (see panelLayoutTrash.ts) to both maps at once. */
  const applyTrashResult = useCallback((result: PanelTrashResult) => {
    setPanelLayouts(result.layouts);
    setPanelTrash(result.trash);
  }, [setPanelLayouts, setPanelTrash]);

  return {
    panelLayouts,
    panelLayoutsRef,
    setPanelLayouts,
    panelTrash,
    panelTrashRef,
    loaded,
    loadFailed,
    applyTrashResult,
  };
}
